import React, { useEffect, useMemo, useState } from 'react';
import {
  Card,
  Table,
  Button,
  Space,
  Modal,
  Form,
  Input,
  Select,
  InputNumber,
  Switch,
  Tag,
  message,
  Popconfirm,
  TreeSelect,
  Upload,
  Image,
  Empty,
} from 'antd';
import { PlusOutlined, EditOutlined, DeleteOutlined, ReloadOutlined, AppstoreOutlined, UploadOutlined } from '@ant-design/icons';
import type { UploadProps } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import {
  type ServiceCategory,
  createServiceCategory,
  deleteServiceCategory,
  getServiceCategoryTree,
  serviceSourceMap,
  serviceSourceOptions,
  updateServiceCategory,
} from '../../services/serviceCategoryService';
import { uploadFile, normalizeFileUrl } from '../../services/fileService';

const ROOT_ID = '0';

const ServiceCategoryPage: React.FC = () => {
  const [loading, setLoading] = useState(false);
  const [submitLoading, setSubmitLoading] = useState(false);
  const [iconUploading, setIconUploading] = useState(false);
  const [categories, setCategories] = useState<ServiceCategory[]>([]);
  const [modalVisible, setModalVisible] = useState(false);
  const [editingCategory, setEditingCategory] = useState<ServiceCategory | null>(null);
  const [form] = Form.useForm();

  const currentIcon = Form.useWatch('icon', form);
  const currentParentId = Form.useWatch('parentId', form);

  const flatCategories = useMemo(() => {
    const result: ServiceCategory[] = [];
    const walk = (list: ServiceCategory[]) => {
      list.forEach((item) => {
        result.push(item);
        if (item.children?.length) {
          walk(item.children);
        }
      });
    };
    walk(categories);
    return result;
  }, [categories]);

  const categoryMap = useMemo(
    () => new Map(flatCategories.map((item) => [item.id, item])),
    [flatCategories],
  );

  const currentParentCategory = useMemo(() => {
    if (!currentParentId || currentParentId === ROOT_ID) {
      return null;
    }
    return categoryMap.get(currentParentId) || null;
  }, [categoryMap, currentParentId]);

  const descendantsMap = useMemo(() => {
    const map = new Map<string, Set<string>>();
    const walk = (node: ServiceCategory): Set<string> => {
      const result = new Set<string>();
      (node.children || []).forEach((child) => {
        result.add(child.id);
        walk(child).forEach((id) => result.add(id));
      });
      map.set(node.id, result);
      return result;
    };
    categories.forEach((item) => walk(item));
    return map;
  }, [categories]);

  const loadData = async () => {
    setLoading(true);
    try {
      const res = await getServiceCategoryTree();
      setCategories(res.data || []);
    } catch {
      message.error('加载服务分类失败');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  useEffect(() => {
    if (currentParentCategory) {
      form.setFieldValue('source', currentParentCategory.source);
    }
  }, [currentParentCategory, form]);

  const handleAdd = (parentId?: string) => {
    setEditingCategory(null);
    form.resetFields();
    const parentCategory = parentId ? categoryMap.get(parentId) : null;
    form.setFieldsValue({
      parentId: parentId || ROOT_ID,
      description: '',
      sort: 0,
      status: true,
      source: parentCategory?.source || 'wechat',
    });
    setModalVisible(true);
  };

  const handleEdit = (record: ServiceCategory) => {
    setEditingCategory(record);
    form.setFieldsValue({
      ...record,
      parentId: record.parentId || ROOT_ID,
      status: record.status === 1,
    });
    setModalVisible(true);
  };

  const handleDelete = async (id: string) => {
    try {
      await deleteServiceCategory(id);
      message.success('删除成功');
      loadData();
    } catch (error: any) {
      message.error(error?.response?.data?.message || '删除失败');
    }
  };

  const handleIconUpload: UploadProps['customRequest'] = async (options) => {
    const { file, onSuccess, onError } = options;
    setIconUploading(true);
    try {
      const res = await uploadFile(file as File, 'service_category_icon');
      form.setFieldsValue({ icon: res.data.url });
      message.success('分类图片上传成功');
      onSuccess?.(res.data);
    } catch (error: any) {
      message.error(error?.response?.data?.message || '分类图片上传失败');
      onError?.(error);
    } finally {
      setIconUploading(false);
    }
  };

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      setSubmitLoading(true);
      const payload = {
        ...values,
        parentId: values.parentId || ROOT_ID,
        source: currentParentCategory?.source || values.source,
        status: values.status ? 1 : 0,
      };
      if (editingCategory) {
        await updateServiceCategory(editingCategory.id, payload);
        message.success('更新成功');
      } else {
        await createServiceCategory(payload);
        message.success('创建成功');
      }
      setModalVisible(false);
      loadData();
    } catch {
      // ignore
    } finally {
      setSubmitLoading(false);
    }
  };

  const buildTreeSelectData = () => {
    const excludedIds = new Set<string>();
    if (editingCategory) {
      excludedIds.add(editingCategory.id);
      descendantsMap.get(editingCategory.id)?.forEach((id) => excludedIds.add(id));
    }

    const buildNodes = (list: ServiceCategory[]): any[] => list
      .filter((item) => !excludedIds.has(item.id))
      .map((item) => ({
        value: item.id,
        title: item.name,
        children: buildNodes(item.children || []),
      }));

    return [{ value: ROOT_ID, title: '顶级分类', children: buildNodes(categories) }];
  };

  const columns: ColumnsType<ServiceCategory> = [
    {
      title: '分类名称',
      dataIndex: 'name',
      key: 'name',
      width: 220,
    },
    {
      title: '分类说明',
      dataIndex: 'description',
      key: 'description',
      width: 260,
      ellipsis: true,
      render: (description?: string) => description || '-',
    },
    {
      title: '分类图片',
      dataIndex: 'icon',
      key: 'icon',
      width: 140,
      render: (icon: string) => icon ? <Image src={normalizeFileUrl(icon)} width={56} height={56} style={{ objectFit: 'cover', borderRadius: 8 }} /> : '-',
    },
    {
      title: '来源',
      dataIndex: 'source',
      key: 'source',
      width: 140,
      render: (source: string) => <Tag color="blue">{serviceSourceMap[source] || source}</Tag>,
    },
    {
      title: '排序',
      dataIndex: 'sort',
      key: 'sort',
      width: 90,
    },
    {
      title: '状态',
      dataIndex: 'status',
      key: 'status',
      width: 90,
      render: (status: number) => <Tag color={status === 1 ? 'green' : 'red'}>{status === 1 ? '启用' : '禁用'}</Tag>,
    },
    {
      title: '操作',
      key: 'action',
      width: 220,
      render: (_, record) => (
        <Space>
          <Button size="small" type="link" onClick={() => handleAdd(record.id)}>添加子分类</Button>
          <Button size="small" icon={<EditOutlined />} onClick={() => handleEdit(record)}>编辑</Button>
          <Popconfirm title="确定删除该分类吗？" onConfirm={() => handleDelete(record.id)}>
            <Button size="small" danger icon={<DeleteOutlined />}>删除</Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <div style={{ padding: 24 }}>
      <Card
        title={<><AppstoreOutlined /> 服务分类</>}
        extra={(
          <Space>
            <Button icon={<ReloadOutlined />} onClick={loadData}>刷新</Button>
            <Button type="primary" icon={<PlusOutlined />} onClick={() => handleAdd()}>
              新增分类
            </Button>
          </Space>
        )}
      >
        <Table
          columns={columns}
          dataSource={categories}
          rowKey="id"
          loading={loading}
          pagination={false}
          expandable={{ defaultExpandAllRows: true }}
        />
      </Card>

      <Modal
        title={editingCategory ? '编辑服务分类' : '新增服务分类'}
        open={modalVisible}
        onCancel={() => setModalVisible(false)}
        onOk={handleSubmit}
        confirmLoading={submitLoading}
        width={640}
        destroyOnHidden
      >
        <Form form={form} layout="vertical" initialValues={{ parentId: ROOT_ID, sort: 0, status: true, source: 'wechat' }}>
          <Form.Item name="parentId" label="上级分类" rules={[{ required: true, message: '请选择上级分类' }]}>
            <TreeSelect treeData={buildTreeSelectData()} treeDefaultExpandAll placeholder="请选择上级分类" />
          </Form.Item>
          <Form.Item name="name" label="分类名称" rules={[{ required: true, message: '请输入分类名称' }]}>
            <Input placeholder="请输入分类名称" maxLength={100} />
          </Form.Item>
          <Form.Item name="description" label="分类说明">
            <Input.TextArea
              placeholder="请输入分类说明"
              maxLength={500}
              showCount
              autoSize={{ minRows: 3, maxRows: 5 }}
            />
          </Form.Item>
          <Form.Item name="icon" label="分类图片" rules={[{ required: true, message: '请上传分类图片' }]}>
            <Input placeholder="请上传分类图片" readOnly />
          </Form.Item>
          <Form.Item>
            <Space orientation="vertical" size={12} style={{ width: '100%' }}>
              <Upload accept="image/*" showUploadList={false} customRequest={handleIconUpload}>
                <Button loading={iconUploading} icon={<UploadOutlined />}>
                  上传分类图片
                </Button>
              </Upload>
              {currentIcon ? (
                <Image
                  src={normalizeFileUrl(currentIcon)}
                  alt="分类图片"
                  width={120}
                  style={{ borderRadius: 8, border: '1px solid #f0f0f0' }}
                />
              ) : (
                <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="暂无分类图片" />
              )}
              <div style={{ color: '#8c8c8c', fontSize: 12 }}>
                用户小程序首页分类图标展示尺寸：86rpx × 86rpx，建议上传正方形图片
              </div>
            </Space>
          </Form.Item>
          <Form.Item
            name="source"
            label="来源"
            rules={[{ required: true, message: '请选择来源' }]}
            extra={currentParentCategory ? `已继承父分类来源：${serviceSourceMap[currentParentCategory.source] || currentParentCategory.source}` : undefined}
          >
            <Select options={serviceSourceOptions} placeholder="请选择来源" disabled={!!currentParentCategory} />
          </Form.Item>
          <Form.Item name="sort" label="排序">
            <InputNumber min={0} style={{ width: '100%' }} placeholder="数字越小越靠前" />
          </Form.Item>
          <Form.Item name="status" label="状态" valuePropName="checked">
            <Switch checkedChildren="启用" unCheckedChildren="禁用" />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default ServiceCategoryPage;
